import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/apiConfig';
import buildImageUrl from '../../config/imageUrlHelper';
import buildSoundUrl from '../../config/soundUrlHelper';
import { categoriasConfig } from '../../config/categoriasConfig';
import { ReactComponent as FiftyIcon } from '../../assets/icons/fifty.svg';
import RankingUsuarios from './RankingUsuarios';

// Juego diario de los usuarios: mismas 12 preguntas para todos, un intento al
// día y sin configuración. El servidor corrige cada respuesta y mide el tiempo,
// aquí solo se muestra lo que devuelve (ver api/utils/juego_diario.php).

const DIFICULTAD = {
  facil: { texto: 'Fácil', clase: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  medio: { texto: 'Media', clase: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' },
  dificil: { texto: 'Difícil', clase: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
};

const LETRAS = ['A', 'B', 'C', 'D'];

const MOTIVO_FIN = {
  fallo: 'Fallaste una pregunta.',
  tiempo: 'Se agotó el tiempo.',
  completada: '¡Has acertado las 12 preguntas!',
};

const mensajeError = (error, porDefecto) => error?.response?.data?.message || porDefecto;

const formatearCuentaAtras = (segundos) => {
  const s = Math.max(0, Math.floor(segundos));
  const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const seg = String(s % 60).padStart(2, '0');
  return `${h}:${m}:${seg}`;
};

const SONIDO_TENSION = '/sounds/Gameshow Background Tension.mp3';

const reproducir = (ruta) => {
  try {
    const audio = new Audio(buildSoundUrl(ruta));
    audio.volume = 0.7;
    audio.play().catch(() => {});
  } catch {
    // sin sonido
  }
};

function CuentaAtrasManana({ fin }) {
  const [restantes, setRestantes] = useState(Math.max(0, (fin - Date.now()) / 1000));
  useEffect(() => {
    const id = setInterval(() => setRestantes(Math.max(0, (fin - Date.now()) / 1000)), 1000);
    return () => clearInterval(id);
  }, [fin]);
  return <span className="font-mono font-semibold">{formatearCuentaAtras(restantes)}</span>;
}

function Reglas({ reglas, repetible }) {
  const d = reglas?.distribucion || { facil: 5, medio: 4, dificil: 3 };
  const filas = [
    ['Preguntas', `${reglas?.total ?? 12} (${d.facil} fáciles, ${d.medio} medias, ${d.dificil} difíciles)`],
    ['Temas', Object.values(categoriasConfig).map((c) => c.nombre).sort((a, b) => a.localeCompare(b, 'es')).join(', ')],
    ['Tiempo', `${reglas?.segundos_por_pregunta ?? 30} segundos por pregunta`],
    ['Comodines', '50:50 (uno por partida)'],
    ['Fin', 'Al primer fallo o si se acaba el tiempo'],
    ['Intentos', `${repetible ? 'Sin límite en cuentas de prueba' : 'Uno al día'}. Las preguntas cambian cada día y son las mismas para todos`],
  ];
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
      {filas.map(([dt, dd]) => (
        <React.Fragment key={dt}>
          <dt className="font-medium text-gray-500 dark:text-gray-400">{dt}</dt>
          <dd className="text-gray-800 dark:text-gray-100 mb-1 sm:mb-0">{dd}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export default function JuegoDiario() {
  const [vista, setVista] = useState('jugar');
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [partida, setPartida] = useState(null);
  const [pregunta, setPregunta] = useState(null);
  const [eliminadas, setEliminadas] = useState([]);
  const [seleccion, setSeleccion] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [restantes, setRestantes] = useState(0);
  const [confirmarInicio, setConfirmarInicio] = useState(false);
  const [recargarRanking, setRecargarRanking] = useState(0);
  const respondidaRef = useRef(false);
  const tensionRef = useRef(null);

  // Mismo volumen que el concurso principal. Se reutiliza el elemento porque
  // arrancarlo dentro de un clic es lo que permite sonar en móviles.
  const arrancarTension = useCallback(() => {
    if (!tensionRef.current) {
      tensionRef.current = new Audio(buildSoundUrl(SONIDO_TENSION));
      tensionRef.current.loop = true;
      tensionRef.current.volume = 0.3;
    }
    tensionRef.current.currentTime = 0;
    tensionRef.current.play()?.catch(() => {});
  }, []);

  const pararTension = useCallback(() => {
    tensionRef.current?.pause();
  }, []);

  const mostrarPregunta = useCallback((nueva, nuevaPartida) => {
    setPregunta(nueva);
    setPartida(nuevaPartida);
    setEliminadas(nueva?.eliminadas || []);
    setSeleccion(null);
    setResultado(null);
    respondidaRef.current = false;
    setRestantes(nueva?.segundos_restantes ?? 0);
  }, []);

  const cargarEstado = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/juego_diario_estado.php`);
      setEstado({ ...res.data, finDelDia: Date.now() + (res.data.segundos_hasta_manana || 0) * 1000 });
      setPartida(res.data.partida);
      if (res.data.pregunta) {
        mostrarPregunta(res.data.pregunta, res.data.partida);
      } else {
        setPregunta(null);
      }
    } catch (e) {
      setError(mensajeError(e, 'No se pudo cargar el juego diario.'));
    } finally {
      setCargando(false);
    }
  }, [mostrarPregunta]);

  useEffect(() => { cargarEstado(); }, [cargarEstado]);

  const responder = useCallback(async (respuesta) => {
    if (respondidaRef.current) return;
    respondidaRef.current = true;
    setEnviando(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/juego_diario_responder.php`, { respuesta });
      setResultado({ ...res.data, elegida: respuesta });
      setPartida(res.data.partida);
      reproducir(res.data.correcta ? '/sounds/acierto-sound.mp3' : '/sounds/error-sound.mp3');
      if (res.data.partida?.estado === 'terminada') {
        setRecargarRanking((n) => n + 1);
      }
    } catch (e) {
      setError(mensajeError(e, 'No se pudo enviar la respuesta.'));
      await cargarEstado();
    } finally {
      setEnviando(false);
    }
  }, [cargarEstado]);

  // Cronómetro de la pregunta en pantalla. Al llegar a 0 se envía sin respuesta.
  useEffect(() => {
    if (!pregunta || resultado) return undefined;
    const fin = Date.now() + (pregunta.segundos_restantes ?? 0) * 1000;
    const id = setInterval(() => {
      const quedan = Math.max(0, (fin - Date.now()) / 1000);
      setRestantes(quedan);
      if (quedan <= 0) {
        clearInterval(id);
        responder(null);
      }
    }, 200);
    return () => clearInterval(id);
  }, [pregunta, resultado, responder]);

  // Suena mientras haya una pregunta sin responder en pantalla y vuelve a empezar en cada una.
  const preguntaSinResponder = vista === 'jugar' && !!pregunta && !resultado;
  useEffect(() => {
    if (preguntaSinResponder) {
      arrancarTension();
    } else {
      pararTension();
    }
  }, [preguntaSinResponder, pregunta, arrancarTension, pararTension]);

  useEffect(() => pararTension, [pararTension]);

  const iniciar = async () => {
    arrancarTension();
    setConfirmarInicio(false);
    setEnviando(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/juego_diario_iniciar.php`);
      setEstado((prev) => ({ ...prev, puede_jugar: false }));
      mostrarPregunta(res.data.pregunta, res.data.partida);
    } catch (e) {
      pararTension();
      setError(mensajeError(e, 'No se pudo empezar la partida.'));
      await cargarEstado();
    } finally {
      setEnviando(false);
    }
  };

  const siguiente = async () => {
    arrancarTension();
    setEnviando(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/juego_diario_siguiente.php`);
      mostrarPregunta(res.data.pregunta, res.data.partida);
    } catch (e) {
      pararTension();
      setError(mensajeError(e, 'No se pudo cargar la siguiente pregunta.'));
      await cargarEstado();
    } finally {
      setEnviando(false);
    }
  };

  const usarCincuenta = async () => {
    setEnviando(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/juego_diario_cincuenta.php`);
      setEliminadas(res.data.eliminadas || []);
      setPartida((prev) => ({ ...prev, comodin_usado: true, comodines_restantes: 0 }));
      setSeleccion((prev) => ((res.data.eliminadas || []).includes(prev) ? null : prev));
    } catch (e) {
      setError(mensajeError(e, 'No se pudo usar el comodín.'));
    } finally {
      setEnviando(false);
    }
  };

  const pestanas = (
    <div className="flex gap-1 mb-5 border-b border-gray-200 dark:border-gray-700">
      {[['jugar', 'Jugar'], ['ranking', 'Ranking de usuarios']].map(([clave, texto]) => (
        <button
          key={clave}
          type="button"
          onClick={() => setVista(clave)}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${vista === clave
            ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
        >
          {texto}
        </button>
      ))}
    </div>
  );

  const cabecera = (
    <header className="mb-4 flex items-center gap-2">
      <svg width="26" height="26" viewBox="0 0 64 64" fill="none" className="text-yellow-400 shrink-0" aria-hidden="true">
        <path d="M20 8H44V18C44 25.732 38.627 32 32 32C25.373 32 20 25.732 20 18V8Z" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
        <path d="M20 12H12C12 20 16 24 22 24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M44 12H52C52 20 48 24 42 24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M32 32V42" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        <path d="M24 54H40" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        <circle cx="32" cy="20" r="4" fill="currentColor" />
      </svg>
      <div>
        <h3 className="text-xl font-semibold dark:text-white">Concurso diario</h3>
        {estado?.fecha && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {new Date(`${estado.fecha}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        )}
      </div>
    </header>
  );

  if (vista === 'ranking') {
    return (
      <div className="dark:text-white">
        {cabecera}
        {pestanas}
        <RankingUsuarios recargarClave={recargarRanking} />
      </div>
    );
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Cargando concurso...</p>
        </div>
      );
    }

    if (!estado) {
      return (
        <button type="button" onClick={cargarEstado} className="text-sm text-blue-600 dark:text-blue-400 underline">
          Reintentar
        </button>
      );
    }

    // Pregunta en juego (o recién respondida)
    if (pregunta) {
      const terminada = partida?.estado === 'terminada';
      const dif = DIFICULTAD[pregunta.dificultad];
      const porcentaje = Math.max(0, Math.min(100, (restantes / pregunta.segundos_por_pregunta) * 100));
      const urgente = restantes <= 10;

      return (
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-semibold text-gray-700 dark:text-gray-200 text-sm">Pregunta {pregunta.numero} de {pregunta.total}</span>
            {dif && <span className={`px-2 py-0.5 rounded-full font-medium ${dif.clase}`}>{dif.texto}</span>}
            {pregunta.categoria && (
              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                {categoriasConfig[pregunta.categoria]?.nombre || pregunta.categoria}
              </span>
            )}
            <span className="ml-auto text-gray-600 dark:text-gray-300">Aciertos: <strong>{partida?.aciertos ?? 0}</strong></span>
          </div>

          {!resultado && (
            <div>
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                  className={`h-full transition-[width] duration-200 ease-linear ${urgente ? 'bg-red-500' : 'bg-blue-500'}`}
                  style={{ width: `${porcentaje}%` }}
                />
              </div>
              <p className={`mt-1 text-right text-sm font-mono ${urgente ? 'text-red-600 dark:text-red-400 font-bold' : 'text-gray-500 dark:text-gray-400'}`}>
                {Math.ceil(restantes)} s
              </p>
            </div>
          )}

          <div className="rounded-xl bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{pregunta.pregunta}</p>
            {pregunta.imagen_url && (
              <img
                src={buildImageUrl(pregunta.imagen_url)}
                alt=""
                className="mt-3 max-h-56 mx-auto rounded-lg object-contain"
              />
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {pregunta.respuestas.map((texto, i) => {
              const eliminada = eliminadas.includes(texto);
              let clase = 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-blue-400 dark:hover:border-blue-500';
              if (resultado) {
                if (texto === resultado.respuesta_correcta) clase = 'border-green-500 bg-green-50 dark:bg-green-900/30';
                else if (texto === resultado.elegida) clase = 'border-red-500 bg-red-50 dark:bg-red-900/30';
                else clase = 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 opacity-60';
              } else if (texto === seleccion) {
                clase = 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-400/50';
              }
              return (
                <button
                  key={`${i}-${texto}`}
                  type="button"
                  disabled={eliminada || !!resultado || enviando}
                  onClick={() => setSeleccion(texto)}
                  className={`flex items-center gap-3 text-left p-3 rounded-lg border-2 transition-colors disabled:cursor-default ${eliminada ? 'invisible' : ''} ${clase}`}
                >
                  <span className="shrink-0 w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-bold text-gray-700 dark:text-gray-200">
                    {LETRAS[i]}
                  </span>
                  <span className="text-sm sm:text-base text-gray-900 dark:text-white">{texto}</span>
                </button>
              );
            })}
          </div>

          {!resultado && (
            <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3">
              <button
                type="button"
                onClick={usarCincuenta}
                disabled={partida?.comodin_usado || enviando}
                title={partida?.comodin_usado ? 'Ya has usado el 50:50 hoy' : 'Quita dos respuestas falsas'}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-green-600 text-white font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed dark:bg-green-700 dark:hover:bg-green-800 dark:disabled:bg-gray-600"
              >
                <FiftyIcon className="w-5 h-5" aria-hidden="true" />
                50:50
              </button>
              <button
                type="button"
                onClick={() => responder(seleccion)}
                disabled={seleccion === null || enviando}
                className="sm:ml-auto w-full sm:w-auto bg-blue-600 text-white py-3 px-6 rounded font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-700 dark:hover:bg-blue-800"
              >
                Confirmar respuesta
              </button>
            </div>
          )}

          {resultado && (
            <div className="space-y-3">
              <div className={`rounded-lg p-3 text-sm ${resultado.correcta
                ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200'}`}
              >
                <p className="font-semibold">
                  {resultado.correcta ? '¡Correcto!' : resultado.elegida === null ? 'Se acabó el tiempo.' : 'Respuesta incorrecta.'}
                </p>
                {resultado.explicacion && <p className="mt-1">{resultado.explicacion}</p>}
              </div>
              {terminada ? (
                <button
                  type="button"
                  onClick={() => { setPregunta(null); setResultado(null); }}
                  className="w-full sm:w-auto bg-blue-600 text-white py-3 px-6 rounded font-semibold hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
                >
                  Ver resultado
                </button>
              ) : (
                <button
                  type="button"
                  onClick={siguiente}
                  disabled={enviando}
                  className="w-full sm:w-auto bg-blue-600 text-white py-3 px-6 rounded font-semibold hover:bg-blue-700 disabled:opacity-50 dark:bg-blue-700 dark:hover:bg-blue-800"
                >
                  Siguiente pregunta
                </button>
              )}
            </div>
          )}
        </div>
      );
    }

    // Acertó la anterior y recargó la página antes de pedir la siguiente
    if (partida?.esperando_siguiente) {
      return (
        <div className="max-w-xl mx-auto text-center space-y-4 py-6">
          <p className="text-lg dark:text-white">
            Llevas <strong>{partida.aciertos}</strong> {partida.aciertos === 1 ? 'acierto' : 'aciertos'}. Te quedan {(estado.reglas?.total ?? 12) - partida.aciertos} preguntas.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">El cronómetro empieza en cuanto aparezca la pregunta.</p>
          <button
            type="button"
            onClick={siguiente}
            disabled={enviando}
            className="bg-blue-600 text-white py-3 px-6 rounded font-semibold hover:bg-blue-700 disabled:opacity-50 dark:bg-blue-700 dark:hover:bg-blue-800"
          >
            Continuar con la pregunta {partida.pregunta_actual + 1}
          </button>
        </div>
      );
    }

    // Partida de hoy terminada
    if (partida?.estado === 'terminada') {
      const total = estado.reglas?.total ?? 12;
      return (
        <div className="max-w-xl mx-auto text-center space-y-4 py-6">
          <h4 className="text-2xl font-bold dark:text-white">Partida de hoy terminada</h4>
          <p className="text-gray-600 dark:text-gray-300">{MOTIVO_FIN[partida.motivo_fin] || ''}</p>
          <div className="flex justify-center gap-8">
            <div>
              <div className="text-4xl font-extrabold text-blue-600 dark:text-blue-400">{partida.aciertos}<span className="text-lg text-gray-400">/{total}</span></div>
              <div className="text-xs text-gray-500 dark:text-gray-400">aciertos</div>
            </div>
            <div>
              <div className="text-4xl font-extrabold text-emerald-600 dark:text-emerald-400">{partida.comodines_restantes}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">comodines restantes</div>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Nuevas preguntas en <CuentaAtrasManana fin={estado.finDelDia} />
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-2">
            {estado.puede_repetir && (
              <button
                type="button"
                onClick={iniciar}
                disabled={enviando}
                className="bg-green-600 text-white py-3 px-6 rounded font-semibold hover:bg-green-700 disabled:opacity-50 dark:bg-green-700 dark:hover:bg-green-800"
              >
                Volver a jugar
              </button>
            )}
            <button
              type="button"
              onClick={() => setVista('ranking')}
              className="bg-blue-600 text-white py-3 px-6 rounded font-semibold hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
            >
              Ver ranking de usuarios
            </button>
          </div>
        </div>
      );
    }

    // Aún no ha jugado hoy
    return (
      <div className="max-w-2xl space-y-5">
        <p className="text-gray-700 dark:text-gray-200">
          Cada día hay un concurso nuevo con las mismas preguntas para todos. Tus aciertos cuentan en el ranking de hoy, que se reinicia cada día.
        </p>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
          <Reglas reglas={estado.reglas} repetible={estado.puede_repetir} />
        </div>
        {confirmarInicio ? (
          <div className="rounded-lg border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20 p-4 space-y-3">
            <p className="text-sm text-yellow-900 dark:text-yellow-100">
              {estado.puede_repetir ? 'Podrás repetir al terminar.' : 'Solo tienes un intento hoy.'} Si sales a mitad de una pregunta, el tiempo sigue corriendo. ¿Empezamos?
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={iniciar}
                disabled={enviando}
                className="bg-green-600 text-white py-2 px-5 rounded font-semibold hover:bg-green-700 disabled:opacity-50 dark:bg-green-700 dark:hover:bg-green-800"
              >
                Empezar
              </button>
              <button
                type="button"
                onClick={() => setConfirmarInicio(false)}
                className="py-2 px-5 rounded font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmarInicio(true)}
            className="w-full sm:w-auto bg-green-600 text-white py-3 px-6 rounded hover:bg-green-700 transition-colors duration-300 font-semibold dark:bg-green-700 dark:hover:bg-green-800"
          >
            Jugar el concurso de hoy
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="dark:text-white">
      {cabecera}
      {pestanas}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200 p-3 text-sm">{error}</div>
      )}
      {renderContenido()}
    </div>
  );
}
